import { Map, Store, Briefcase, Network, ArrowRight } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";

const PILLARS = [
  {
    icon: Map, n: "01", title: "Manage Your Destination",
    lead: "Bring your destination supply into one structured platform.",
    items: ["Destinations", "Hotels", "Activities", "Transfers", "Packages", "Seasonal pricing", "Media library", "Content management"],
    tid: "pillar-manage",
  },
  {
    icon: Store, n: "02", title: "Sell Your Destination",
    lead: "Turn your destination catalogue into a digital storefront.",
    items: ["White-label website", "Custom branding", "Custom domain", "Destination pages", "Package pages", "SEO", "Lead capture", "Enquiry forms"],
    tid: "pillar-sell",
  },
  {
    icon: Briefcase, n: "03", title: "Operate Your Business",
    lead: "Replace scattered spreadsheets, emails and WhatsApp threads with one workspace.",
    items: ["Inquiry management", "Customer management", "Quotation engine", "Team assignment", "Status tracking", "Notes & documents", "Communication tracking"],
    tid: "pillar-operate",
  },
];

const ROUTES = [
  { tag: "B2C", path: ["Your DMC", "Travel-Tek", "Your branded website", "Traveller"] },
  { tag: "B2B", path: ["Your DMC", "Travel-Tek", "Agent / Partner", "Traveller"] },
];

export default function Pillars() {
  return (
    <section id="pillars" className="py-24 lg:py-32">
      <div className="container-x">
        <Reveal><Chapter index={5} label="Four Product Pillars" /></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-8 max-w-2xl font-serif text-3xl leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Manage it. Sell it. Operate it. Grow it.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {PILLARS.map((p, i) => (
            <Reveal key={p.n} delay={0.06 * i}>
              <div data-testid={p.tid} className="group h-full rounded-2xl border border-sand bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-terra/50 sm:p-8">
                <div className="flex items-center justify-between">
                  <p.icon className="h-5 w-5 text-terra" />
                  <span className="font-serif text-3xl text-terra/30">{p.n}</span>
                </div>
                <h3 className="mt-4 font-serif text-xl text-ink sm:text-2xl">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-stone2">{p.lead}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {p.items.map((it) => (
                    <span key={it} className="rounded-full bg-paper px-2.5 py-1 text-[11px] font-medium text-stone2">{it}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.18}>
            <div data-testid="pillar-grow" className="grain relative h-full overflow-hidden rounded-2xl bg-ink p-7 text-paper sm:p-8">
              <div className="flex items-center justify-between">
                <Network className="h-5 w-5 text-terra" />
                <span className="font-serif text-3xl text-terra/40">04</span>
              </div>
              <h3 className="mt-4 font-serif text-xl sm:text-2xl">Grow Your Distribution</h3>
              <p className="mt-2 text-sm leading-relaxed text-paper/60">
                Give travel agents a digital way to discover your destination products,
                submit enquiries and work with your team — while keeping your brand
                and commercial control.
              </p>
              <div className="mt-5 space-y-2.5">
                {ROUTES.map((r) => (
                  <div key={r.tag} className="rounded-xl border border-white/10 bg-white/5 p-3.5" data-testid={`route-${r.tag.toLowerCase()}`}>
                    <p className="mb-2 font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-terra">{r.tag}</p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {r.path.map((n, i) => (
                        <span key={n} className="flex items-center gap-1.5">
                          <span className={`rounded-lg px-2.5 py-1 text-[10px] font-semibold ${i === 0 ? "bg-terra text-ink" : "bg-white/10 text-paper/85"}`}>{n}</span>
                          {i < r.path.length - 1 && <ArrowRight className="h-3 w-3 text-terra/70" />}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
